/**
 * Durable local storage for the counter, on IndexedDB.
 *
 * **Why not localStorage.** It is synchronous, capped at a few megabytes, and —
 * the part that matters here — it serialises on the main thread, so the write
 * that records a sale competes with the render that prints it. IndexedDB is
 * transactional and its writes are durable once the transaction commits, which
 * is the property this whole feature rests on: a bill that has been taken must
 * survive the tab being closed, the browser being killed, and the power going
 * out between one customer and the next.
 *
 * **Why no library.** Two object stores and four operations. A wrapper would be
 * more code to audit than the code it replaced.
 */

import { log } from './log';

const DB_NAME = 'tlp-counter';
const DB_VERSION = 1;

/**
 * How long IndexedDB gets to answer before it is treated as broken.
 *
 * `indexedDB.open` does not always fail loudly. It can fire `blocked` and sit
 * there — another tab holding an older connection, a profile where site data is
 * disabled, a browser mid-eviction — and a request that fires neither `success`
 * nor `error` leaves a promise pending for the life of the page. Downstream
 * that was a Save button that spun forever with a customer at the counter,
 * which is the one failure this whole subsystem exists to prevent.
 *
 * A till that cannot reach its own storage in five seconds is not slow, it is
 * broken, and the operator has to be told that rather than shown a spinner.
 */
const OPEN_TIMEOUT = 5_000;
const TX_TIMEOUT = 5_000;

/** Rejects if a promise has not settled in time, so nothing here can hang. */
function withDeadline<T>(work: Promise<T>, ms: number, what: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const bail = setTimeout(
      () =>
        reject(
          new Error(
            `This device's storage did not answer (${what}). The browser may have site data turned off.`,
          ),
        ),
      ms,
    );
    work.then(resolve, reject).finally(() => clearTimeout(bail));
  });
}

/** Queued writes waiting to reach the server. Append-only until they succeed. */
export const OUTBOX = 'outbox';
/** Reference data taken while online, so the till can work without a network. */
export const CACHE = 'cache';

let connection: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  if (connection) return connection;

  const opening = new Promise<IDBDatabase>((resolve, reject) => {
    if (!globalThis.indexedDB) {
      reject(new Error('This browser has no IndexedDB, so nothing can be queued on this device.'));
      return;
    }

    const started = performance.now();
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(OUTBOX)) {
        /*
         * `seq` is an auto-increment key, and it is what guarantees replay
         * order. Sales must reach the server in the order they were taken or
         * the bill numbers arrive out of sequence.
         */
        db.createObjectStore(OUTBOX, { keyPath: 'seq', autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(CACHE)) {
        db.createObjectStore(CACHE);
      }
    };

    /*
     * The handler whose absence hung the till. `blocked` fires INSTEAD of
     * success or error when another tab is holding an older connection, and
     * without it the open request simply never answers.
     */
    request.onblocked = () =>
      reject(
        new Error(
          'Another tab of this app is holding the local database. Close the other tabs and reload.',
        ),
      );

    request.onsuccess = () => {
      log.cache(`storage open in ${Math.round(performance.now() - started)}ms`);
      const db = request.result;
      /*
       * A connection the browser closes underneath us — eviction, a profile
       * reset, "clear site data" — must not be handed out again, or every write
       * afterwards fails against a dead handle.
       */
      db.onclose = () => {
        log.warn('cache', 'storage connection closed by the browser');
        connection = null;
      };
      resolve(db);
    };
    request.onerror = () => reject(request.error ?? new Error('Storage refused to open'));
  });

  /*
   * A failed open is NOT cached. The old code kept the rejected promise
   * forever, so one transient failure at startup — a browser still restoring
   * the profile, say — poisoned local storage for the whole session with no way
   * back but a reload.
   */
  connection = withDeadline(opening, OPEN_TIMEOUT, 'opening the database').catch((error) => {
    connection = null;
    log.error('cache', 'storage failed to open', error);
    throw error;
  });

  return connection;
}

async function tx<T>(
  store: string,
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await open();

  const work = new Promise<T>((resolve, reject) => {
    const transaction = db.transaction(store, mode);
    const request = run(transaction.objectStore(store));

    /*
     * Resolved on the TRANSACTION completing, not on the request succeeding.
     * A request can succeed inside a transaction that then aborts, and taking
     * the request as the signal is how you end up believing a sale was stored
     * when it was rolled back.
     */
    transaction.oncomplete = () => resolve(request.result);
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error ?? new Error('Transaction aborted'));
  });

  // A transaction can also stall indefinitely — most often behind a long-lived
  // one in another tab. Same rule: answer, or be reported as broken.
  return withDeadline(work, TX_TIMEOUT, `${mode} on ${store}`);
}

export const store = {
  async put<T>(name: string, value: T, key?: IDBValidKey): Promise<IDBValidKey> {
    return tx(name, 'readwrite', (s) => (key === undefined ? s.put(value) : s.put(value, key)));
  },

  async get<T>(name: string, key: IDBValidKey): Promise<T | undefined> {
    return tx(name, 'readonly', (s) => s.get(key) as IDBRequest<T | undefined>);
  },

  async all<T>(name: string): Promise<T[]> {
    return tx(name, 'readonly', (s) => s.getAll() as IDBRequest<T[]>);
  },

  async remove(name: string, key: IDBValidKey): Promise<void> {
    await tx(name, 'readwrite', (s) => s.delete(key) as unknown as IDBRequest<undefined>);
  },

  async count(name: string): Promise<number> {
    return tx(name, 'readonly', (s) => s.count());
  },
};

/**
 * Whether this browser can store anything durably at all.
 *
 * Private windows and some locked-down enterprise profiles disable IndexedDB
 * outright. The counter must be told rather than discovering it the first time
 * the network drops with a bill half-written.
 */
export async function storageAvailable(): Promise<boolean> {
  return (await probeStorage()).ok;
}

/**
 * The same question, with the answer to "why not" attached.
 *
 * A boolean was enough for the status pill and useless for anything else: a
 * pharmacist told "this device cannot store bills" needs to know whether to
 * close a second tab, turn site data back on, or leave private browsing. The
 * settings screen shows this verbatim.
 *
 * It writes and deletes a real row rather than only opening the database.
 * Opening succeeds in several profiles that then refuse every write, and
 * finding that out at the moment a bill is taken is exactly too late.
 */
export async function probeStorage(): Promise<{ ok: boolean; reason?: string }> {
  try {
    await store.put(CACHE, { at: Date.now() }, '__probe');
    await store.remove(CACHE, '__probe');
    return { ok: true };
  } catch (error) {
    return { ok: false, reason: error instanceof Error ? error.message : String(error) };
  }
}

/** Roughly how much this origin is using, for the settings screen. */
export async function storageEstimate(): Promise<{ usage: number; quota: number } | null> {
  if (!navigator.storage?.estimate) return null;
  try {
    const { usage, quota } = await navigator.storage.estimate();
    return { usage: usage ?? 0, quota: quota ?? 0 };
  } catch {
    return null;
  }
}

/** Whether the browser has already promised not to evict this origin. */
export async function durabilityGranted(): Promise<boolean> {
  if (!navigator.storage?.persisted) return false;
  try {
    return await navigator.storage.persisted();
  } catch {
    return false;
  }
}

/**
 * Asks the browser not to evict this origin's storage under disk pressure.
 *
 * Best-effort and unprompted in most browsers. Without it, IndexedDB is
 * "best-effort" storage and may be cleared — which for a queue of unsent bills
 * would be silent data loss.
 */
export async function requestDurability(): Promise<boolean> {
  if (!navigator.storage?.persist) return false;
  try {
    if (await navigator.storage.persisted()) return true;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}
